/** PermGate — route-level permission guard. Renders children only when the user
 *  holds ANY of the given permission codes in the active workspace.
 *  Feb 2026 · Also supports `module="brands"` so an entire route is gated by
 *  the workspace module kill-switch — used to hide `/app/brands` when the
 *  Brand module is turned off in workspace settings. */
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import { ShieldOff } from "lucide-react";

export default function PermGate({ anyOf = [], module: moduleKey, children }) {
  const { can, ready, modules } = useWorkspacePerms();
  if (!ready) return null;
  if (moduleKey && modules && modules[moduleKey] === false) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center" data-testid="module-disabled">
        <div className="w-14 h-14 border border-border flex items-center justify-center mb-4">
          <ShieldOff className="w-6 h-6 text-foreground/50" />
        </div>
        <h2 className="text-lg font-bold mb-1">Module turned off</h2>
        <p className="text-sm text-foreground/60 max-w-md">
          This module has been disabled for your workspace. Ask an admin to re-enable it in Workspace settings.
        </p>
      </div>
    );
  }
  if (anyOf.length && !anyOf.some((c) => can(c))) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center" data-testid="perm-denied">
        <div className="w-14 h-14 border border-border flex items-center justify-center mb-4">
          <ShieldOff className="w-6 h-6 text-foreground/50" />
        </div>
        <h2 className="text-lg font-bold mb-1">Access restricted</h2>
        <p className="text-sm text-foreground/60 max-w-md">
          Your role doesn&apos;t include access to this module. Ask a workspace admin if you believe you should have it.
        </p>
      </div>
    );
  }
  return children;
}
